import { useEffect, useState } from 'react'
import { ArrowUp, ArrowDown, Minus } from 'lucide-react'
import { formatAltitude, formatSpeed, formatHeading, formatVerticalRate, getAirlineFromCallsign } from '../lib/utils'

export default function MatrixDisplay({ flights, weather }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  
  useEffect(() => {
    if (flights.length <= 1) return
    const interval = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setCurrentIndex(i => (i + 1) % flights.length)
        setVisible(true)
      }, 500)
    }, 8000)
    return () => clearInterval(interval)
  }, [flights.length])
  
  const flight = flights[currentIndex] || {}
  const airline = getAirlineFromCallsign(flight.callsign)
  const vr = flight.vertical_rate || 0
  
  return (
    <div className="h-screen w-screen bg-matrix-dark matrix-grid flex flex-col items-center justify-center relative overflow-hidden">
      <div className="scanline-overlay" />
      <div className="absolute top-6 left-0 right-0 text-center">
        <div className="matrix-text text-matrix-green/60 text-sm tracking-[0.3em] uppercase">
          FlightWall Matrix // Live ADS-B Feed
        </div>
        {weather && (
          <div className="matrix-text text-matrix-green/40 text-xs mt-1 tracking-wider">
            METAR {weather.icao} {weather.raw?.substring(0, 30)}...
          </div>
        )}
      </div>
      <div className={`transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="text-center mb-8">
          <div className="matrix-text text-matrix-green text-6xl md:text-8xl font-bold tracking-tight mb-2">
            {airline.code}
          </div>
          <div className="matrix-text text-matrix-green/80 text-2xl md:text-3xl tracking-widest">
            {flight.callsign || 'NO SIGNAL'}
          </div>
          {flight.route && (
            <div className="matrix-text text-matrix-green/60 text-lg mt-2 tracking-wider">
              {flight.route[0]} → {flight.route[1]}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 mt-8">
          <MetricBox label="ALT" value={formatAltitude(flight.altitude)} />
          <MetricBox label="SPD" value={formatSpeed(flight.velocity ? flight.velocity * 2.23694 : 0)} />
          <MetricBox label="TRK" value={formatHeading(flight.true_track)} />
          <MetricBox label="V/S" value={formatVerticalRate(vr)} icon={
            vr > 100 ? <ArrowUp className="w-4 h-4" /> : 
            vr < -100 ? <ArrowDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />
          } />
        </div>
        <div className="text-center mt-8">
          <div className="matrix-text text-matrix-green/50 text-sm tracking-wider">
            {flight.distance ? `${flight.distance} NM DISTANCE` : 'SCANNING...'}
          </div>
        </div>
      </div>
      <div className="absolute bottom-6 left-0 right-0 flex justify-between px-8">
        <div className="matrix-text text-matrix-green/30 text-xs">{flights.length} AIRCRAFT IN SECTOR</div>
        <div className="matrix-text text-matrix-green/30 text-xs">
          UPDATED {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      </div>
      {flights.length > 1 && (
        <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-2">
          {flights.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === currentIndex ? 'bg-matrix-green' : 'bg-matrix-green/20'}`} />
          ))}
        </div>
      )}
    </div>
  )
}

function MetricBox({ label, value, icon }) {
  return (
    <div className="text-center">
      <div className="matrix-text text-matrix-green/40 text-xs tracking-[0.2em] mb-1">{label}</div>
      <div className="matrix-text text-matrix-green text-2xl md:text-4xl font-bold flex items-center justify-center gap-2">
        {icon}{value}
      </div>
    </div>
  )
}
